import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const getAuthHeader = () => {
  const token = localStorage.getItem('mideast_token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const API = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = API ? API.replace('/api', '').replace(/\/$/, '') : '';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-PK', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true });
};
const statusMeta = (s) => ({
  completed:   { label:'Completed',   color:'#22C55E', bg:'#22C55E18' },
  scheduled:   { label:'Scheduled',   color:'#38BDF8', bg:'#38BDF818' },
  in_progress: { label:'In Progress', color:'#F59E0B', bg:'#F59E0B18' },
  cancelled:   { label:'Cancelled',   color:'#F87171', bg:'#F8717118' },
}[s] || { label:s||'Unknown', color:'#94A3B8', bg:'transparent' });

const calcAvgServiceTime = (jobs) => {
  const valid = jobs.filter(j => j.status==='completed' && j.work_started_at && j.work_finished_at);
  if (!valid.length) return null;
  const avg = valid.reduce((acc,j) => acc+(new Date(j.work_finished_at)-new Date(j.work_started_at))/60000, 0) / valid.length;
  const h=Math.floor(avg/60), m=Math.round(avg%60);
  return m>0 ? `${h}h ${m}m` : `${h}h`;
};
const calcPanelsThisMonth = (jobs) => {
  const now=new Date();
  return jobs.filter(j => { const d=new Date(j.scheduled_date); return j.status==='completed' && d.getFullYear()===now.getFullYear() && d.getMonth()===now.getMonth(); })
    .reduce((acc,j) => acc+parseInt(j.fsr_panels_cleaned||j.total_panels_cleaned||0), 0);
};
const buildDailyData = (jobs, monthKey) => {
  if (!monthKey) return [];
  const [yr,mo]=monthKey.split('-').map(Number);
  const days=new Date(yr,mo,0).getDate();
  const map={};
  for (let d=1;d<=days;d++) map[d]={day:d,total:0,completed:0,scheduled:0};
  jobs.forEach(j => {
    const d=new Date(j.scheduled_date);
    if (d.getFullYear()===yr && d.getMonth()+1===mo) {
      const day=d.getDate();
      if (map[day]) { map[day].total++; if(j.status==='completed') map[day].completed++; if(j.status==='scheduled') map[day].scheduled++; }
    }
  });
  return Object.values(map);
};

const StatusBadge = ({status}) => { const sm=statusMeta(status); return <span className="cp-pill" style={{background:sm.bg,color:sm.color,fontSize:10}}>{sm.label}</span>; };
const ThemeIcon = ({isDark}) => isDark ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
);

// ═══════════════════════════════════════════════════════════════
// GTA SLD VIEWER (POWERED BY FRAMER MOTION)
// ═══════════════════════════════════════════════════════════════
const GTASLDViewer = ({ photos, sldData, baseUrl, siteName, onViewChange }) => {
  const [view, setView]                 = useState('map');
  const [activePoint, setActivePoint]   = useState(null);
  const [photoType, setPhotoType]       = useState('before');
  const [navDir, setNavDir]             = useState('fwd');
  const [mapPhase, setMapPhase]         = useState('visible');
  const [photoPhase, setPhotoPhase]     = useState('hidden');
  const [zoomOrigin, setZoomOrigin]     = useState('50% 50%');
  const [hoveredPin, setHoveredPin]     = useState(null);
  const [hoveredArrow, setHoveredArrow] = useState(null);
  const [isWalking, setIsWalking]       = useState(false);

  const getUrl = (p) => {
    if (!p?.photo_url) return '';
    if (p.photo_url.startsWith('http')) return p.photo_url;
    return `${baseUrl}${p.photo_url.startsWith('/')?'':'/'}${p.photo_url}`;
  };

  useEffect(() => {
    photos?.forEach(p => { if(p.photo_url){const i=new Image();i.src=getUrl(p);} });
  }, [photos]);

  const {pointList, diagramUrl} = useMemo(() => {
    let dUrl=sldData?.diagram_url||null;
    const pts=sldData?.points||[];
    if (dUrl && !dUrl.startsWith('http')) dUrl=`${baseUrl}${dUrl.startsWith('/')?'':'/'}${dUrl}`;
    return {
      diagramUrl: dUrl,
      pointList: pts.map((pt,i) => {
        const pp=photos.filter(p=>p.point_id===pt.id||p.point_label===pt.label);
        return { id:pt.id, index:i+1, label:pt.label, x_percent:pt.x_percent, y_percent:pt.y_percent,
          hasPhoto:pp.length>0, before:pp.find(p=>p.photo_type==='before')||null, after:pp.find(p=>p.photo_type==='after')||null };
      })
    };
  }, [photos, sldData, baseUrl]);

  const curIdx      = pointList.findIndex(p=>p.id===activePoint?.id);
  const canGoBack   = curIdx > 0;
  const canGoFwd    = curIdx < pointList.length-1;

  const handlePinClick = (pt) => {
    if (mapPhase !== 'visible') return;
    setZoomOrigin(`${pt.x_percent??50}% ${pt.y_percent??50}%`);
    setMapPhase('zooming');
    setTimeout(() => {
      setMapPhase('hidden');
      setNavDir('fwd');
      setActivePoint(pt);
      setPhotoType(pt.before ? 'before' : 'after');
      setPhotoPhase('entering');
      if(onViewChange) onViewChange('photo');
      
      setIsWalking(true);
      setTimeout(() => setIsWalking(false), 1100); 

      setTimeout(() => { setView('photo'); setPhotoPhase('visible'); }, 30);
    }, 650);
  };

  const handleBack = () => {
    if (photoPhase!=='visible') return;
    setPhotoPhase('leaving');
    if(onViewChange) onViewChange('map');
    setTimeout(() => {
      setPhotoPhase('hidden');
      setView('map');
      setMapPhase('zoomback');
      setTimeout(() => setMapPhase('visible'), 600);
    }, 380);
  };

  const navigate = (idx, dir) => {
    setNavDir(dir);
    const next = pointList[idx];
    const nextType = next.before ? 'before' : 'after';
    setActivePoint(next);
    setPhotoType(nextType);
    
    setIsWalking(true);
    setTimeout(() => setIsWalking(false), 1100); 
  };

  const mapStyle = () => {
    const base = { position:'absolute', inset:0, width:'100%', height:'100%', background:'#0D1120', overflow:'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' };
    if (mapPhase==='visible')   return { ...base, transform:'scale(1)', opacity:1, filter:'blur(0)', transition:'none' };
    if (mapPhase==='zooming')   return { ...base, transformOrigin:zoomOrigin, transform:'scale(3)', opacity:0, filter:'blur(5px)', transition:'transform 0.65s cubic-bezier(0.4,0,0.2,1), opacity 0.65s ease, filter 0.65s ease' };
    if (mapPhase==='hidden')    return { ...base, display:'none' };
    if (mapPhase==='zoomback')  return { ...base, transformOrigin:zoomOrigin, transform:'scale(1)', opacity:1, filter:'blur(0)', transition:'transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease 0.1s' };
    return base;
  };

  const photoStyle = () => {
    const base = { position:'absolute', inset:0, background:'#050810' };
    if (photoPhase==='hidden')  return { ...base, opacity:0, transform:'scale(1.05)', transition:'none', pointerEvents:'none' };
    if (photoPhase==='entering')return { ...base, opacity:0, transform:'scale(1.05)', transition:'none', pointerEvents:'none' };
    if (photoPhase==='visible') return { ...base, opacity:1, transform:'scale(1)',    transition:'opacity 0.45s ease, transform 0.45s cubic-bezier(0.4,0,0.2,1)', pointerEvents:'all' };
    if (photoPhase==='leaving') return { ...base, opacity:0, transform:'scale(1.05)', transition:'opacity 0.38s ease, transform 0.38s ease', pointerEvents:'none' };
    return base;
  };

  const ArrowBtn = ({ side, onClick, label }) => {
    const isH = hoveredArrow===side;
    return (
      <div
        onClick={onClick}
        onMouseEnter={()=>setHoveredArrow(side)}
        onMouseLeave={()=>setHoveredArrow(null)}
        style={{
          position:'absolute', top:0, bottom:0, [side]:0, width:'15%', zIndex:30,
          display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
          background: isH
            ? `linear-gradient(${side==='left'?'90':'270'}deg, rgba(0,0,0,0.55) 0%, transparent 100%)`
            : `linear-gradient(${side==='left'?'90':'270'}deg, rgba(0,0,0,0.20) 0%, transparent 100%)`,
          transition:'background 0.25s ease',
        }}
      >
        <div style={{
          width:56, height:56, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
          background: isH ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.42)',
          border:`2px solid ${isH?'#fff':'rgba(255,255,255,0.60)'}`,
          backdropFilter:'blur(6px)', color:'#fff',
          boxShadow: isH ? '0 0 28px rgba(255,255,255,0.20)' : '0 2px 14px rgba(0,0,0,0.45)',
          transform: isH ? 'scale(1.13)' : 'scale(1)',
          transition:'all 0.22s ease',
        }}>
          {side==='left'
            ? <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            : <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          }
        </div>
        {isH && (
          <div style={{ position:'absolute', bottom:28, left:'50%', transform:'translateX(-50%)', background:'rgba(0,0,0,0.82)', border:'1px solid rgba(255,255,255,0.14)', borderRadius:7, padding:'4px 11px', fontSize:11, fontWeight:700, color:'#fff', whiteSpace:'nowrap', backdropFilter:'blur(6px)', animation:'fadeUp 0.14s ease' }}>
            {side==='left' ? `← ${label}` : `${label} →`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ 
      display: view === 'photo' ? 'grid' : 'block', 
      gridTemplateColumns: view === 'photo' ? '1fr 300px' : '1fr', 
      gap: 20, 
      height: 600,
      transition: 'all 0.4s ease'
    }}>

      {/* ── MAIN AREA ── */}
      <div style={{ position:'relative', borderRadius:16, overflow:'hidden', background:'#0D1120', height:'100%', boxShadow:'0 10px 40px rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.07)' }}>
        
        {/* MAP PHASE */}
        <div style={mapStyle()}>
          {diagramUrl ? (
            <div className="sld-image-container">
              <img src={diagramUrl} alt="SLD" className="gta-map-img" draggable={false} />
              {pointList.map((pt) => {
                const isH=hoveredPin===pt.id;
                return (
                  <div key={pt.id}
                    onMouseEnter={()=>setHoveredPin(pt.id)}
                    onMouseLeave={()=>setHoveredPin(null)}
                    onClick={()=>handlePinClick(pt)}
                    style={{ position:'absolute', left:`${pt.x_percent??50}%`, top:`${pt.y_percent??50}%`, transform:'translate(-50%,-50%)', display:'flex', flexDirection:'column', alignItems:'center', gap:3, cursor:pt.hasPhoto?'pointer':'default', zIndex:isH?30:10 }}
                  >
                    {isH && (
                      <div style={{ position:'absolute', bottom:'108%', left:'50%', transform:'translateX(-50%)', background:'rgba(8,12,24,0.92)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'5px 12px', fontSize:11, fontWeight:700, color:'#E2E8F0', whiteSpace:'nowrap', backdropFilter:'blur(8px)', boxShadow:'0 4px 16px rgba(0,0,0,0.5)', animation:'fadeUp 0.14s ease', pointerEvents:'none' }}>
                        {pt.label||`Point ${pt.index}`}{pt.hasPhoto&&<span style={{color:'#38BDF8',marginLeft:6}}>▶ View</span>}
                      </div>
                    )}
                    <div style={{ width:isH?44:34, height:isH?44:34, borderRadius:'50%', background:pt.hasPhoto?(isH?'rgba(34,211,165,0.38)':'rgba(34,211,165,0.18)'):'rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.2s ease', animation:pt.hasPhoto?'pinPulse 2.5s ease-in-out infinite':'none' }}>
                      <div style={{ width:isH?30:24, height:isH?30:24, borderRadius:'50%', background:pt.hasPhoto?'#22D3A5':'rgba(255,255,255,0.28)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, color:'#080C18', fontFamily:'monospace', boxShadow:isH?'0 0 0 3px rgba(255,255,255,0.3),0 0 18px rgba(34,211,165,0.8)':'0 0 0 2px rgba(0,0,0,0.5),0 3px 8px rgba(0,0,0,0.4)', transition:'all 0.2s ease' }}>{pt.index}</div>
                    </div>
                    {pt.label && <div style={{ background:'rgba(8,12,24,0.88)', borderRadius:4, padding:'2px 7px', fontSize:9, fontWeight:700, color:'#E2E8F0', whiteSpace:'nowrap', border:'1px solid rgba(255,255,255,0.10)', backdropFilter:'blur(4px)', pointerEvents:'none', opacity:isH?1:0.78, transition:'opacity 0.2s' }}>{pt.label}</div>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',color:'rgba(255,255,255,0.28)',flexDirection:'column',gap:10}}><span style={{fontSize:28}}>🗺️</span><span style={{fontWeight:600}}>No SLD Diagram uploaded</span></div>
          )}

          <div style={{ position:'absolute', top:16, left:16, color:'#38BDF8', fontSize:13, fontWeight:800, letterSpacing:1, textTransform:'uppercase', background:'rgba(15,23,42,0.88)', padding:'6px 14px', borderRadius:8, border:'1px solid rgba(56,189,248,0.30)', zIndex:10 }}>
            SLD: {siteName||'Site'}
          </div>
          <div className="sv-mobile-hint">Click a pin to view photos</div>
        </div>

        {/* PHOTO PHASE */}
        <div style={photoStyle()}>
          {activePoint && (
            <>
              <AnimatePresence initial={false} custom={navDir}>
                <motion.img
                  key={activePoint.id + photoType}
                  src={getUrl(activePoint[photoType])}
                  alt={activePoint.label}
                  draggable={false}
                  custom={navDir}
                  variants={{
                    enter: (dir) => ({
                      scale: dir === 'fade' ? 1 : (dir === 'fwd' ? 0.6 : 1.4),
                      opacity: 0,
                      filter: dir === 'fade' ? 'blur(10px)' : 'blur(30px)',
                      zIndex: dir === 'fwd' ? 1 : 3
                    }),
                    center: { scale: 1, opacity: 1, filter: 'blur(0px)', zIndex: 2 },
                    exit: (dir) => ({
                      scale: dir === 'fade' ? 1 : (dir === 'fwd' ? 1.4 : 0.6),
                      opacity: 0,
                      filter: dir === 'fade' ? 'blur(10px)' : 'blur(30px)',
                      zIndex: dir === 'fwd' ? 3 : 1
                    })
                  }}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                />
              </AnimatePresence>

              <div style={{ zIndex: 40, position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <button onClick={handleBack} className="sv-back-btn" style={{ pointerEvents: 'all' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  <span>Back to Map</span>
                </button>

                <div style={{ position:'absolute', top:18, left:'50%', transform:'translateX(-50%)', display:'flex', gap:8, pointerEvents: 'all' }}>
                  {activePoint.before && (
                    <button onClick={()=>{setNavDir('fade');setPhotoType('before');}} style={{ padding:'7px 22px', borderRadius:20, fontSize:12, fontWeight:800, cursor:'pointer', background:photoType==='before'?'#F59E0B':'rgba(0,0,0,0.65)', color:photoType==='before'?'#000':'rgba(255,255,255,0.75)', border:`1px solid ${photoType==='before'?'#F59E0B':'rgba(255,255,255,0.2)'}`, backdropFilter:'blur(8px)', transition:'all 0.2s', boxShadow:photoType==='before'?'0 4px 15px rgba(245,158,11,0.4)':'none' }}>Before</button>
                  )}
                  {activePoint.after && (
                    <button onClick={()=>{setNavDir('fade');setPhotoType('after');}} style={{ padding:'7px 22px', borderRadius:20, fontSize:12, fontWeight:800, cursor:'pointer', background:photoType==='after'?'#22C55E':'rgba(0,0,0,0.65)', color:photoType==='after'?'#000':'rgba(255,255,255,0.75)', border:`1px solid ${photoType==='after'?'#22C55E':'rgba(255,255,255,0.2)'}`, backdropFilter:'blur(8px)', transition:'all 0.2s', boxShadow:photoType==='after'?'0 4px 15px rgba(34,197,94,0.4)':'none' }}>After</button>
                  )}
                </div>

                {canGoBack && <div style={{pointerEvents: 'all'}}><ArrowBtn side="left" onClick={(e)=>{e.stopPropagation();navigate(curIdx-1,'bwd');}} label={pointList[curIdx-1]?.label||`Point ${curIdx}`}/></div>}
                {canGoFwd  && <div style={{pointerEvents: 'all'}}><ArrowBtn side="right" onClick={(e)=>{e.stopPropagation();navigate(curIdx+1,'fwd');}} label={pointList[curIdx+1]?.label||`Point ${curIdx+2}`}/></div>}

                <div className="sv-bottom-bar" style={{pointerEvents: 'all'}}>
                  <span style={{fontWeight:800,color:'#fff',fontSize:14}}>{activePoint.label||`Point ${activePoint.index}`}</span>
                  <span style={{ fontSize:9, fontWeight:800, letterSpacing:1, padding:'3px 8px', borderRadius:4, textTransform:'uppercase', background:photoType==='before'?'rgba(245,158,11,0.25)':'rgba(34,197,94,0.25)', color:photoType==='before'?'#FCD34D':'#86EFAC' }}>{photoType}</span>
                  <span style={{fontSize:11,color:'rgba(255,255,255,0.45)',fontFamily:'monospace'}}>{curIdx+1} / {pointList.length}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── MINI MAP WIDGET ── */}
      {view === 'photo' && (
        <div className="cp-fade" style={{ position: 'relative', borderRadius: 16, background: '#0D1120', border: '1px solid rgba(255,255,255,0.07)', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', top: 12, left: 12, fontSize: 10, fontWeight: 800, color: '#38BDF8', letterSpacing: 1, textTransform: 'uppercase', background: 'rgba(15,23,42,0.88)', padding: '5px 12px', borderRadius: 6, zIndex: 10, backdropFilter: 'blur(4px)', border: '1px solid rgba(56,189,248,0.2)' }}>
            📍 Live Tracking
          </div>
          
          {diagramUrl ? (
            <div className="sld-image-container" style={{ transform: 'scale(0.85)' }}>
              <img src={diagramUrl} alt="SLD" className="gta-map-img" draggable={false} style={{ opacity: 0.6 }} />
              
              {pointList.map(pt => (
                <div key={pt.id} style={{ position: 'absolute', left: `${pt.x_percent}%`, top: `${pt.y_percent}%`, transform: 'translate(-50%,-50%)', width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />
              ))}

              {activePoint && (
                <motion.div
                  animate={{ left: `${activePoint.x_percent}%`, top: `${activePoint.y_percent}%` }}
                  transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }} 
                  style={{ position: 'absolute', transform: 'translate(-50%, -100%)', zIndex: 20 }}
                >
                  <div style={{ 
                    background: 'rgba(15,23,42,0.8)', padding: '6px', borderRadius: '50%', 
                    border: '2px solid #38BDF8', display: 'flex', alignItems: 'center', 
                    justifyContent: 'center', boxShadow: '0 0 20px rgba(56,189,248,0.9)', 
                    backdropFilter: 'blur(4px)',
                    animation: isWalking ? 'bobWalk 0.4s infinite alternate ease-in-out' : 'none'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="5" r="2.5"></circle>
                      <line x1="12" y1="7.5" x2="12" y2="14"></line>
                      <line x1="12" y1="9" x2="12" y2="16" style={{ transformOrigin: '12px 9px', transform: isWalking ? 'none' : 'rotate(-20deg)', animation: isWalking ? 'swingArm 0.4s infinite alternate-reverse ease-in-out' : 'none' }}></line>
                      <line x1="12" y1="9" x2="12" y2="16" style={{ transformOrigin: '12px 9px', transform: isWalking ? 'none' : 'rotate(20deg)', animation: isWalking ? 'swingArm 0.4s infinite alternate ease-in-out' : 'none' }}></line>
                      <line x1="12" y1="14" x2="12" y2="22" style={{ transformOrigin: '12px 14px', transform: isWalking ? 'none' : 'rotate(-20deg)', animation: isWalking ? 'swingLeg 0.4s infinite alternate ease-in-out' : 'none' }}></line>
                      <line x1="12" y1="14" x2="12" y2="22" style={{ transformOrigin: '12px 14px', transform: isWalking ? 'none' : 'rotate(20deg)', animation: isWalking ? 'swingLeg 0.4s infinite alternate-reverse ease-in-out' : 'none' }}></line>
                    </svg>
                  </div>
                  <div style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)', background: '#38BDF8', color: '#000', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap', boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>
                    You are here
                  </div>
                </motion.div>
              )}
            </div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, fontWeight: 600 }}>No Diagram Uploaded</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── DAILY BAR CHART ──────────────────────────────────────────────
const DailyBarChart = ({ dailyData, T, isDark, monthLabel }) => {
  const canvasRef=useRef(null), chartRef=useRef(null);
  useEffect(() => {
    if (!canvasRef.current||!dailyData||!dailyData.length) return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new window.Chart(canvasRef.current.getContext('2d'), {
      type:'bar',
      data:{ labels:dailyData.map(d=>d.day), datasets:[
        {label:'Completed',data:dailyData.map(d=>d.completed),backgroundColor:'rgba(34,197,94,0.85)',borderRadius:5},
        {label:'Scheduled',data:dailyData.map(d=>d.scheduled),backgroundColor:'rgba(56,189,248,0.7)',borderRadius:5},
      ]},
      options:{ responsive:true, maintainAspectRatio:false, plugins:{legend:{display:false}},
        scales:{ x:{grid:{display:false},ticks:{color:isDark?'rgba(255,255,255,0.4)':'#666'}}, y:{beginAtZero:true,grid:{color:isDark?'rgba(255,255,255,0.05)':'#eee'},ticks:{stepSize:1}} } }
    });
    return ()=>{ if(chartRef.current) chartRef.current.destroy(); };
  }, [dailyData, isDark]);
  if (!dailyData||!dailyData.length) return <div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:T.muted}}>No data</div>;
  return <div style={{position:'relative',height:200}}><canvas ref={canvasRef}/></div>;
};

// ─── RESPONSIVE CSS INJECTION ─────────────────────────────────────
const getCSS = (isDark) => `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  html,body{font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;background:${isDark?'#080D18':'#F1F5F9'};width:100%;}
  ::-webkit-scrollbar{width:5px;height:5px;}::-webkit-scrollbar-track{background:transparent;}
  ::-webkit-scrollbar-thumb{background:${isDark?'rgba(255,255,255,0.12)':'rgba(0,0,0,0.15)'};border-radius:10px;}
  
  .cp-fade{animation:cpFade 0.4s ease both;}
  @keyframes cpFade{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
  
  .cp-card{background:${isDark?'#0F1825':'#FFFFFF'};border:1px solid ${isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.08)'};border-radius:16px;transition:border-color 0.2s;}
  .cp-pill{display:inline-flex;align-items:center;gap:5px;padding:5px 13px;border-radius:20px;font-size:11px;font-weight:600;white-space:nowrap;}
  
  .cp-tab{padding:8px 22px;border-radius:9px;border:none;cursor:pointer;font-size:14px;font-weight:600;font-family:'Inter',sans-serif;transition:all 0.2s;}
  .cp-tab.active{background:#38BDF8;color:#000;}
  .cp-tab.inactive{background:transparent;color:${isDark?'rgba(241,245,249,0.5)':'rgba(15,22,35,0.5)'};}
  
  .cp-job-row{padding:15px 22px;border-bottom:1px solid ${isDark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.06)'};cursor:pointer;display:grid;grid-template-columns:1fr auto;gap:12px;align-items:center;}
  .cp-job-row:hover{background:rgba(56,189,248,0.05);}
  .cp-job-row.selected{background:rgba(56,189,248,0.08);border-left:3px solid #38BDF8;}
  
  .cp-site-card{background:${isDark?'rgba(255,255,255,0.03)':'#FFFFFF'};border:1px solid ${isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.08)'};border-radius:12px;overflow:hidden;cursor:pointer;transition:all 0.2s;}
  .cp-site-card:hover{background:${isDark?'rgba(56,189,248,0.08)':'rgba(56,189,248,0.05)'};border-color:rgba(56,189,248,0.4);}
  .cp-site-card.active{border-color:#38BDF8;background:rgba(56,189,248,0.1);}
  
  .cp-stat{background:${isDark?'#0F1825':'#FFFFFF'};border:1px solid ${isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.08)'};border-radius:16px;padding:22px 24px;}
  .cp-input{width:100%;padding:10px 16px;border-radius:10px;border:1px solid ${isDark?'rgba(255,255,255,0.09)':'rgba(0,0,0,0.1)'};background:${isDark?'#0F1825':'#F8FAFC'};color:${isDark?'#F1F5F9':'#0F1623'};outline:none;}
  .cp-spin{animation:spin 0.75s linear infinite;}@keyframes spin{to{transform:rotate(360deg);}}
  
  .theme-toggle{width:40px;height:40px;border-radius:10px;border:1px solid ${isDark?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.12)'};background:${isDark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.04)'};color:${isDark?'#F1F5F9':'#0F1623'};cursor:pointer;display:flex;align-items:center;justify-content:center;}
  .sv-empty{height:600px;display:flex;align-items:center;justify-content:center;background:${isDark?'#0F1825':'#E2E8F0'};border-radius:16px;color:${isDark?'#94A3B8':'#64748B'};font-weight:600;border:1px solid ${isDark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.05)'};}
  
  /* RESPONSIVE LAYOUT CLASSES */
  .cp-main-pad { padding: 28px 32px; width: 100%; }
  .cp-header-wrap { max-width: 100%; display: flex; align-items: center; justify-content: space-between; height: 64px; }
  .cp-header-actions { display: flex; align-items: center; gap: 12px; }
  
  .cp-welcome-banner { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
  .cp-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
  .cp-chart-grid { display: grid; grid-template-columns: 1fr 320px; gap: 18px; margin-bottom: 20px; }
  
  /* GALLERY LAYOUTS */
  .cp-gallery-grid { display: grid; grid-template-columns: 1fr 340px; gap: 20px; }
  .cp-gallery-stack { display: flex; flex-direction: column; gap: 20px; }
  
  /* BEAUTIFIED SITES VERTICAL CONTAINER */
  .cp-sites-vertical-container {
    background: rgba(13, 17, 32, 0.4);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    height: 600px;
    overflow: hidden;
  }
  .cp-sites-header {
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    background: rgba(15, 23, 42, 0.8);
    font-size: 11px;
    font-weight: 800;
    color: #38BDF8;
    letter-spacing: 0.1em;
    backdrop-filter: blur(10px);
    z-index: 10;
  }
  .cp-sites-list {
    padding: 15px;
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .cp-sites-list::-webkit-scrollbar { width: 6px; }
  .cp-sites-list::-webkit-scrollbar-thumb { background: rgba(56,189,248,0.2); border-radius: 10px; }
  .cp-sites-list::-webkit-scrollbar-thumb:hover { background: rgba(56,189,248,0.4); }

  .cp-sites-horizontal { display: flex; gap: 12px; overflow-x: auto; padding-bottom: 12px; }
  .cp-sites-horizontal .cp-site-card { min-width: 240px; flex-shrink: 0; }
  .cp-sites-horizontal::-webkit-scrollbar { height: 6px; }
  .cp-sites-horizontal::-webkit-scrollbar-thumb { background: rgba(56,189,248,0.3); border-radius: 10px; }

  .cp-history-grid { display: grid; gap: 20px; align-items: start; }
  .cp-history-grid.has-selection { grid-template-columns: 1fr 420px; }
  .cp-detail-panel { padding: 22px; position: sticky; top: 76px; max-height: calc(100vh - 108px); overflow-y: auto; }

  @media (max-width: 1100px) {
    .cp-kpi-grid { grid-template-columns: repeat(2, 1fr); }
    .cp-history-grid.has-selection { grid-template-columns: 1fr 350px; }
    .cp-gallery-grid { grid-template-columns: 1fr 300px; }
  }
  @media (max-width: 850px) {
    .cp-chart-grid { grid-template-columns: 1fr; }
    .cp-gallery-grid { grid-template-columns: 1fr; }
    .cp-history-grid.has-selection { display: flex; flex-direction: column-reverse; }
    .cp-detail-panel { position: static; max-height: none; overflow-y: visible; }
  }
  @media (max-width: 600px) {
    .cp-main-pad { padding: 16px 12px; }
    .cp-header-wrap { height: auto; flex-direction: column; align-items: stretch; gap: 14px; padding: 14px 0; }
    .cp-header-actions { justify-content: space-between; width: 100%; }
    .cp-kpi-grid { grid-template-columns: 1fr; }
    .gta-container { height: 450px !important; }
    .cp-tab { padding: 8px 12px; font-size: 12px; }
    .sv-back-btn span { display: none; }
  }

  .sld-image-container { position: relative; display: inline-block; max-width: 100%; max-height: 100%; }
  .gta-map-img { max-width: 100%; max-height: 600px; display: block; object-fit: contain; }
  .mj-fsr-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:16px; }
  .sv-mobile-hint { position:absolute; bottom:16px; left:50%; transform:translateX(-50%); color:rgba(255,255,255,0.38); font-size:11px; font-weight:600; background:rgba(0,0,0,0.58); padding:5px 14px; border-radius:20px; z-index:10; white-space:nowrap; pointer-events:none; }
  .sv-back-btn { position:absolute; top:18px; left:18px; z-index:40; display:flex; align-items:center; gap:7px; padding:8px 16px; border-radius:10px; background:rgba(0,0,0,0.70); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.18); color:#fff; font-weight:700; font-size:12px; cursor:pointer; transition:all 0.2s; }
  .sv-back-btn:hover { background:rgba(56,189,248,0.28); }
  .sv-bottom-bar { position:absolute; bottom:20px; left:50%; transform:translateX(-50%); display:flex; align-items:center; gap:10px; z-index:40; background:rgba(0,0,0,0.75); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.12); padding:8px 18px; border-radius:12px; }
  .mj-fsr-field-label { font-size:9px; font-weight:700; font-family:'DM Mono',monospace; text-transform:uppercase; letter-spacing:0.12em; color:rgba(255,255,255,0.22); margin-bottom:4px; }
  .mj-fsr-field-val { font-size:13px; font-weight:600; color:#E2E8F0; }
  .mj-fsr-bool-yes { color:#86EFAC; } .mj-fsr-bool-no { color:#FCA5A5; }
  .mj-fsr-field { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:8px; padding:10px 12px; }
  
  @keyframes bobWalk {
    0% { transform: translateY(0px); }
    100% { transform: translateY(-4px); }
  }
  @keyframes swingLeg {
    0% { transform: rotate(-35deg); }
    100% { transform: rotate(35deg); }
  }
  @keyframes swingArm {
    0% { transform: rotate(-30deg); }
    100% { transform: rotate(30deg); }
  }
  @keyframes pinPulse{0%,100%{box-shadow:0 0 0 0 rgba(34,211,165,0.5);}60%{box-shadow:0 0 0 10px rgba(34,211,165,0);}}
  @keyframes fadeUp{from{opacity:0;transform:translateX(-50%) translateY(6px);}to{opacity:1;transform:translateX(-50%) translateY(0);}}
`;

// ═══════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════
const MideastCustomerDashboard = () => {
  const [isDark,setIsDark]=useState(true);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const [profile,setProfile]=useState(null);
  const [allJobs,setAllJobs]=useState([]);
  const [activeTab,setActiveTab]=useState('overview');
  const [selectedJob,setSelectedJob]=useState(null);
  const [jobDetail,setJobDetail]=useState(null);
  const [activeSite,setActiveSite]=useState(null);
  const [galleryPhotos,setGalleryPhotos]=useState([]);
  const [sldData,setSldData]=useState(null);
  const [search,setSearch]=useState('');
  const [todayJobs,setTodayJobs]=useState([]);
  const [detailLoading,setDetailLoading]=useState(false);
  
  const [viewerMode, setViewerMode] = useState('map');

  const nowDate=new Date();
  const defaultMonth=`${nowDate.getFullYear()}-${String(nowDate.getMonth()+1).padStart(2,'0')}`;
  const [chartMonth,setChartMonth]=useState(defaultMonth);

  const T={
    bg:isDark?'#080D18':'#F1F5F9', surface:isDark?'#0B1220':'#FFFFFF',
    border:isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.08)',
    text:isDark?'#F1F5F9':'#0F1623', muted:isDark?'rgba(241,245,249,0.45)':'rgba(15,22,35,0.48)',
    accent:'#38BDF8', green:'#22C55E', amber:'#F59E0B', red:'#F87171', purple:'#A78BFA', orange:'#FB923C',
  };

  useEffect(()=>{fetchAll();},[]);

  const fetchAll = async () => {
    try {
      setLoading(true);setError(null);
      const cfg=getAuthHeader();
      const jobsRes=await axios.get(`${API}/mideast/customer-portal/jobs`,cfg).catch(()=>({data:{jobs:[]}}));
      const combined=[...(jobsRes.data.jobs||[])].sort((a,b)=>new Date(b.scheduled_date)-new Date(a.scheduled_date));
      const userData=localStorage.getItem('mideast_user')?JSON.parse(localStorage.getItem('mideast_user')):null;
      const name=userData?.client_name||userData?.name||userData?.company_name||`${userData?.firstname||''} ${userData?.lastname||''}`.trim()||'Customer';
      setProfile({client_name:name,contact_person:userData?.email||userData?.contact_email||'—',contractual_services_count:combined.length});
      setAllJobs(combined);
      const ts=new Date().toDateString();
      setTodayJobs(combined.filter(j=>!isNaN(new Date(j.scheduled_date))&&new Date(j.scheduled_date).toDateString()===ts));
      const done=combined.find(j=>j.status==='completed');
      if(done) handleSelectGallerySite(done);
    } catch { setError('Unable to load portal data.'); }
    finally { setLoading(false); }
  };

  const handleSelectGallerySite = async (job) => {
    setActiveSite(job);setGalleryPhotos([]);setSldData(null); setViewerMode('map');
    try {
      const cfg=getAuthHeader();
      const pr=await axios.get(`${API}/mideast/point-photos/job/${job.id}`,cfg);
      setGalleryPhotos(pr.data.photos||[]);
      let dr=await axios.get(`${API}/mideast/customer-portal/site-diagram/${job.site_id||job.id}`,cfg).catch(()=>null);
      if (dr?.data?.diagram) { setSldData(dr.data.diagram); return; }
      let fb=await axios.get(`${API}/mideast/diagrams/site/${job.site_id||job.id}`,cfg).catch(()=>null);
      if (!fb?.data?.diagram_url) { const aj=await axios.get(`${API}/mideast/jobs/${job.id}`,cfg).catch(()=>null); if(aj?.data?.diagram) fb={data:aj.data.diagram}; }
      if (fb?.data) setSldData(fb.data);
    } catch(e){console.error('Gallery error',e);}
  };

const handleSelectJobDetail = async (j) => {
  setSelectedJob(j); setDetailLoading(true); setJobDetail(null);
  try {
    const cfg = getAuthHeader();
    const jr = await axios.get(
      `${API}/mideast/customer-portal/jobs/${j.schedule_id || j.id}`, cfg
    );
    setJobDetail(jr.data);
  } catch(e) { console.error('Detail error', e); }
  finally { setDetailLoading(false); }
};

  // ✅ SUPER STRONG BOOLEAN PARSER (100% Fix)
const normBool = (v) => {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null || v === undefined) return false;
  const s = String(v).toLowerCase().trim();
  // PostgreSQL sometimes returns 't'/'f' shorthand
  return ['true', 'yes', '1', 'on', 'y', 't'].includes(s);
};  
  
  const FsrBool = ({ label, value }) => (
    <div className="mj-fsr-field">
      <div className="mj-fsr-field-label">{label}</div>
      <div className={`mj-fsr-field-val ${value ? 'mj-fsr-bool-yes' : 'mj-fsr-bool-no'}`}>
        {value ? '✓ Yes' : '✗ No'}
      </div>
    </div>
  );

  const completedJobs=allJobs.filter(j=>j.status==='completed');
  const avgServiceTime=calcAvgServiceTime(allJobs);
  const panelsThisMonth=calcPanelsThisMonth(allJobs);
  const availableMonths=[...new Set(allJobs.map(j=>{const d=new Date(j.scheduled_date);return isNaN(d)?null:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;}).filter(Boolean))].sort((a,b)=>b.localeCompare(a));
  const dailyData=buildDailyData(allJobs,chartMonth);
  const monthLabel=chartMonth?new Date(chartMonth+'-01').toLocaleString('default',{month:'long',year:'numeric'}):'';
  const monthShort=chartMonth?new Date(chartMonth+'-01').toLocaleString('default',{month:'short'}):'';
  const upcomingJobs=allJobs.filter(j=>j.status==='scheduled').slice(0,4);
  const cmJobs=allJobs.filter(j=>{const d=new Date(j.scheduled_date);return d.getMonth()===nowDate.getMonth()&&d.getFullYear()===nowDate.getFullYear();});
  const cmDone=cmJobs.filter(j=>j.status==='completed').length;
  const cmPct=cmJobs.length?Math.round((cmDone/cmJobs.length)*100):0;
  const filteredJobs=allJobs.filter(j=>{if(!search)return true;const q=search.toLowerCase();return j.site_name?.toLowerCase().includes(q)||j.team_name?.toLowerCase().includes(q);});

  if(loading) return(<><style>{getCSS(isDark)}</style><div style={{minHeight:'100vh',background:T.bg,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16}}><div className="cp-spin" style={{width:40,height:40,border:`2px solid ${T.border}`,borderTopColor:T.accent,borderRadius:'50%'}}/><p style={{color:T.muted,fontSize:13,letterSpacing:'0.12em'}}>LOADING PORTAL…</p></div></>);
  if(error) return(<><style>{getCSS(isDark)}</style><div style={{minHeight:'100vh',background:T.bg,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16}}><p style={{color:T.red,fontSize:15}}>{error}</p><button style={{padding:'9px 20px',borderRadius:9,border:'none',background:'#38BDF8',color:'#000',fontWeight:700,cursor:'pointer'}} onClick={fetchAll}>Retry</button></div></>);

  return (
    <>
      <style>{getCSS(isDark)}</style>
      <div style={{minHeight:'100vh',background:T.bg,color:T.text,fontFamily:"'Inter',sans-serif",width:'100%'}}>

        <div style={{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:'0 32px',position:'sticky',top:0,zIndex:40}}>
          <div className="cp-header-wrap">
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:38,height:38,borderRadius:9,background:`linear-gradient(135deg,${T.accent},${T.green})`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
              </div>
              <div>
                <div style={{fontSize:16,fontWeight:700,letterSpacing:'-0.01em',lineHeight:1.1}}>{profile?.client_name||'Customer Portal'}</div>
                <div style={{fontSize:10,color:T.muted,letterSpacing:'0.1em',marginTop:2}}>SOLAR OPERATIONS DASHBOARD</div>
              </div>
            </div>
            <div className="cp-header-actions">
              <div style={{display:'flex',gap:3,background:isDark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.05)',borderRadius:11,padding:4}}>
                {[['overview','Overview'],['gallery','SLD Gallery'],['history','Service History']].map(([tab,lbl])=>(
                  <button key={tab} className={`cp-tab ${activeTab===tab?'active':'inactive'}`} onClick={()=>setActiveTab(tab)}>{lbl}</button>
                ))}
              </div>
              <button className="theme-toggle" onClick={()=>setIsDark(d=>!d)}><ThemeIcon isDark={isDark}/></button>
            </div>
          </div>
        </div>

        <div className="cp-main-pad">

          {activeTab==='overview' && (
            <div className="cp-fade">
              {profile && (
                <div className="cp-welcome-banner" style={{background:isDark?'linear-gradient(120deg,#0F1E38 0%,#0F1825 100%)':'linear-gradient(120deg,#EFF6FF 0%,#F0F9FF 100%)',border:`1px solid rgba(56,189,248,0.2)`,borderRadius:18,padding:'22px 28px',marginBottom:24}}>
                  <div>
                    <div style={{fontSize:12,color:T.muted,marginBottom:5,letterSpacing:'0.1em',fontWeight:600}}>WELCOME BACK</div>
                    <div style={{fontSize:32,fontWeight:900,letterSpacing:'-0.03em',lineHeight:1}}>{profile.client_name}</div>
                    <div style={{fontSize:13,color:T.muted,marginTop:6}}>
                      {profile.contact_person&&<span style={{marginRight:20}}>POC: <strong style={{color:T.text}}>{profile.contact_person}</strong></span>}
                      <span>Total Jobs: <strong style={{color:T.text}}>{profile.contractual_services_count}</strong></span>
                    </div>
                  </div>
                  <div style={{background:'rgba(251,146,60,0.09)',border:'1px solid rgba(251,146,60,0.22)',borderRadius:12,padding:'12px 20px',textAlign:'center',minWidth:120}}>
                    <div style={{fontSize:26,fontWeight:800,color:T.orange,letterSpacing:'-0.02em'}}>{panelsThisMonth>0?panelsThisMonth.toLocaleString():'—'}</div>
                    <div style={{fontSize:10,color:T.muted,letterSpacing:'0.1em',marginTop:3,fontWeight:600}}>PANELS CLEANED THIS MONTH</div>
                  </div>
                </div>
              )}
              <div className="cp-kpi-grid">
                {[
                  {label:'SCHEDULED THIS MONTH',value:cmJobs.length,sub:new Date().toLocaleString('default',{month:'long',year:'numeric'}),color:T.accent},
                  {label:'COMPLETED THIS MONTH',value:cmDone,sub:new Date().toLocaleString('default',{month:'long'}),color:T.green},
                  {label:'AVG SERVICE TIME',value:avgServiceTime||'—',sub:'Start → complete',color:T.purple},
                  {label:'COMPLETION RATE',value:`${cmPct}%`,sub:`${cmDone} of ${cmJobs.length} this month`,color:T.amber},
                ].map((s,i)=>(
                  <div key={i} className="cp-stat" style={{borderTop:`3px solid ${s.color}`}}>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.1em',color:T.muted,marginBottom:10}}>{s.label}</div>
                    <div style={{fontSize:38,fontWeight:800,color:T.text,letterSpacing:'-0.03em',lineHeight:1}}>{s.value}</div>
                    <div style={{fontSize:12,color:T.muted,marginTop:7,fontWeight:500}}>{s.sub}</div>
                  </div>
                ))}
              </div>
              <div className="cp-chart-grid">
                <div className="cp-card" style={{padding:'22px 26px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18}}>
                    <div><div style={{fontSize:15,fontWeight:700}}>Daily Service Activity — {monthLabel}</div><div style={{fontSize:12,color:T.muted,marginTop:3}}>Completed & scheduled</div></div>
                    <select value={chartMonth} onChange={e=>setChartMonth(e.target.value)} style={{padding:'6px 12px',borderRadius:9,background:isDark?'#0B1220':'#F8FAFC',color:T.text,outline:'none',border:`1px solid ${T.border}`}}>
                      {availableMonths.map(m=><option key={m} value={m}>{new Date(m+'-01').toLocaleString('default',{month:'long',year:'numeric'})}</option>)}
                    </select>
                  </div>
                  <DailyBarChart dailyData={dailyData} T={T} isDark={isDark} monthLabel={monthShort}/>
                </div>
                <div className="cp-card" style={{padding:'18px 20px'}}>
                  <div style={{fontSize:11,fontWeight:700,letterSpacing:'0.1em',color:T.muted,marginBottom:14}}>UPCOMING</div>
                  {upcomingJobs.length===0?<div style={{fontSize:13,color:T.muted,textAlign:'center'}}>No upcoming</div>
                    :upcomingJobs.map((j,i)=>(
                      <div key={i} style={{padding:'9px 0',borderBottom:i<upcomingJobs.length-1?`1px solid ${T.border}`:'none'}}>
                        <div style={{fontSize:13,fontWeight:600}}>{j.site_name}</div>
                        <div style={{fontSize:11,color:T.muted}}>{fmtDate(j.scheduled_date)}</div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          )}

          {activeTab==='gallery' && (
            <div className="cp-fade">
              <div style={{marginBottom:20}}>
                <div style={{fontSize:22,fontWeight:800,marginBottom:5}}>Interactive SLD Gallery</div>
                <div style={{fontSize:14,color:T.muted}}>Click a pin → smooth zoom → navigate points with in-image Street View arrows</div>
              </div>
              
              <div className={viewerMode === 'photo' ? 'cp-gallery-stack' : 'cp-gallery-grid'}>
                <div>
                  {activeSite
                    ? <GTASLDViewer photos={galleryPhotos} sldData={sldData} baseUrl={IMG_BASE} siteName={activeSite.site_name} onViewChange={setViewerMode}/>
                    : <div className="sv-empty">Select a completed site to view</div>
                  }
                </div>
                
                <div className={viewerMode === 'photo' ? 'cp-sites-horizontal' : 'cp-sites-vertical-container'}>
                  {viewerMode !== 'photo' && <div className="cp-sites-header">AVAILABLE SITES ({completedJobs.length})</div>}
                  
                  <div className={viewerMode === 'photo' ? '' : 'cp-sites-list'}>
                    {completedJobs.length===0&&<div style={{textAlign:'center',padding:40,color:T.muted}}>No completed jobs yet</div>}
                    {completedJobs.map((j,i)=>{
                      const sm=statusMeta(j.status), isA=activeSite?.id===j.id;
                      return (
                        <div key={i} className={`cp-site-card ${isA?'active':''}`} onClick={()=>handleSelectGallerySite(j)}>
                          <div style={{padding:'14px'}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                              <div style={{fontSize:13,fontWeight:600}}>{j.site_name}</div>
                              <span className="cp-pill" style={{background:sm.bg,color:sm.color,fontSize:9}}>{sm.label}</span>
                            </div>
                            <div style={{fontSize:11,color:T.muted,marginTop:5}}>{fmtDate(j.scheduled_date)}</div>
                            {j.has_diagram&&<div style={{fontSize:10,color:T.accent,marginTop:4}}>📐 SLD Available</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab==='history' && (
            <div className={`cp-history-grid ${selectedJob ? 'has-selection' : ''} cp-fade`}>
              <div>
                <div style={{marginBottom:16}}><input className="cp-input" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search site…"/></div>
                <div className="cp-card">
                  <div style={{padding:'13px 22px',borderBottom:`1px solid ${T.border}`}}><span style={{fontSize:12,color:T.muted,fontWeight:700}}>{filteredJobs.length} SERVICES</span></div>
                  {filteredJobs.map((j,i)=>{
                    const sm=statusMeta(j.status),sel=selectedJob?.id===j.id;
                    return (
                      <div key={i} className={`cp-job-row ${sel?'selected':''}`} onClick={()=>handleSelectJobDetail(j)}>
                        <div><div style={{fontSize:14,fontWeight:600,marginBottom:3}}>{j.site_name}</div><div style={{fontSize:12,color:T.muted}}>{fmtDate(j.scheduled_date)} · {j.team_name||'Unassigned'}</div></div>
                        <span className="cp-pill" style={{background:sm.bg,color:sm.color}}>{sm.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedJob && (() => {
                const sm=statusMeta(selectedJob.status);
                const Row=({label,value})=>(value!=null&&value!==''&&value!=='—')?(<div style={{display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:`1px solid ${T.border}`,fontSize:13}}><span style={{color:T.muted,fontWeight:500}}>{label}</span><span style={{color:T.text,fontWeight:600,textAlign:'right',maxWidth:'55%'}}>{value}</span></div>):null;
                const SH=({label})=>(<div style={{fontSize:10,fontWeight:700,letterSpacing:'0.1em',color:T.muted,marginBottom:8,marginTop:18,paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>{label}</div>);

                return (
                  <div className="cp-card cp-detail-panel">
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}>
                      <div><div style={{fontSize:15,fontWeight:700}}>{selectedJob.site_name}</div><span className="cp-pill" style={{background:sm.bg,color:sm.color,marginTop:6}}>{sm.label}</span></div>
                      <button onClick={()=>{setSelectedJob(null);setJobDetail(null);}} style={{background:'none',border:'none',color:T.muted,cursor:'pointer',fontSize:16}}>✖</button>
                    </div>
                    <SH label="JOB INFORMATION"/>
                    <Row label="Date" value={fmtDate(selectedJob.scheduled_date)}/>
                    <Row label="Client" value={selectedJob.client_name}/>
                    <Row label="Location" value={selectedJob.location}/>
                    
                    {jobDetail&&(jobDetail.reached_site_at||jobDetail.work_started_at||jobDetail.work_finished_at)&&(<>
                      <SH label="TRACKING TIMESTAMPS"/>
                      <Row label="Reached Site"   value={fmtDateTime(jobDetail.reached_site_at)}/>
                      <Row label="Reached Panels" value={fmtDateTime(jobDetail.reached_panels_at)}/>
                      <Row label="Work Started"   value={fmtDateTime(jobDetail.work_started_at)}/>
                      <Row label="Work Finished"  value={fmtDateTime(jobDetail.work_finished_at)}/>
                      <Row label="Site Exited"    value={fmtDateTime(jobDetail.site_exited_at)}/>
                    </>)}

                    {detailLoading?(<div style={{padding:24,textAlign:'center',color:T.muted,display:'flex',flexDirection:'column',alignItems:'center',gap:10}}><div className="cp-spin" style={{width:26,height:26,border:`2px solid ${T.border}`,borderTopColor:T.accent,borderRadius:'50%'}}/>Loading FSR…</div>)
                    :jobDetail?.fsr?(<>
                      <SH label="FIELD SERVICE REPORT"/>
                      <Row label="Panels Cleaned"  value={jobDetail.fsr.panels_cleaned}/>
                      <Row label="Submitted By"    value={jobDetail.fsr.submitted_by_name}/>
                      <Row label="Cable Condition" value={jobDetail.fsr.cable_condition}/>
                      <Row label="Cable Quantity"  value={jobDetail.fsr.cable_quantity}/>
                      <Row label="Panel Brand"     value={jobDetail.fsr.panel_brand}/>
                      <Row label="Alarm Code"      value={jobDetail.fsr.alarm_code}/>
                      <Row label="Shading Details" value={jobDetail.fsr.shading_details}/>

                      {/* ✅ CLEANED UP AND FIXED INSPECTION RESULTS SECTION */}
                      <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', margin:'16px 0 8px' }}>
                        Inspection Results
                      </div>
                      <div className="mj-fsr-grid">
                        <FsrBool label="Panel Damage"      value={normBool(jobDetail.fsr.panel_damage)}/>
                        <FsrBool label="Inverter Alarm"    value={normBool(jobDetail.fsr.inverter_alarm)}/>
                        <FsrBool label="Potential Shading" value={normBool(jobDetail.fsr.potential_shading)}/>
                        <FsrBool label="Rusting"           value={normBool(jobDetail.fsr.rusting)}/>
                        <FsrBool label="Bird Dropping"     value={normBool(jobDetail.fsr.bird_dropping)}/>
                        <FsrBool label="Moss & Debris"     value={normBool(jobDetail.fsr.mos_and_debris)}/>
                        <FsrBool label="Earthing"          value={normBool(jobDetail.fsr.earthing)}/>
                      </div>

                      {jobDetail.fsr.observations&&<Row label="Observations" value={jobDetail.fsr.observations}/>}
                      {jobDetail.fsr.work_done&&<Row label="Work Done" value={jobDetail.fsr.work_done}/>}
                      
                      {jobDetail.fsr.signature_url&&(<div style={{marginTop:20}}>
                        <div style={{fontSize:10,color:T.muted,marginBottom:5,fontWeight:700,letterSpacing:'0.1em'}}>CUSTOMER SIGNATURE</div>
                        <img src={`${IMG_BASE}${jobDetail.fsr.signature_url.startsWith('/')?'':'/'}${jobDetail.fsr.signature_url}`} alt="Signature" style={{width:'100%',height:100,objectFit:'contain',background:'#fff',borderRadius:8}}/>
                      </div>)}
                    </>)
                    :(<div style={{padding:24,textAlign:'center',color:T.muted}}>No FSR submitted yet.</div>)}
                  </div>
                );
              })()}
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default MideastCustomerDashboard;